import type { BigIntish, ChainId } from '@morpho-org/blue-sdk'

import { InterfaceChainUtils } from '@morpho-org/dapp-core'
import { isDefined } from '@morpho-org/morpho-ts'
import { Avatar } from '@morpho-org/ui-v6/client'
import { V1Badge } from '@morpho-org/ui-v6/server'
import { TruncatedTooltip } from '@repo/ui'
import { getAddress } from 'viem'

import { InternalLink } from '@/components/common/InternalLink'
import {
  VaultPendingTimelockChangeCap,
  VaultPendingTimelockCountdown,
  VaultPendingTimelockRemoveMarket
} from '@/components/VaultPage/VaultV1/VaultPendingTimelock/components'
import { StyledVaultPendingTimelock } from '@/components/VaultPage/VaultV1/VaultPendingTimelock/VaultPendingTimelock.components'
import { getVaultUrl } from '@/helpers/routing'

import type { UnderlyingVaultInfo, UnderlyingVaultPendingTimelock } from '../types/pendingTimelocks'

import { HoverableWrapper } from './VaultV2PendingTimelockStyled'

interface UnderlyingVaultPendingTimelockItemProps {
  chainId: ChainId
  pendingTimelock: UnderlyingVaultPendingTimelock
  marketSupplyCaps?: Record<string, BigIntish>
  vault?: UnderlyingVaultInfo
  selectable?: boolean
  onClick?: () => void
}

const UnderlyingVaultPendingTimelockItem = ({
  chainId,
  pendingTimelock,
  marketSupplyCaps,
  vault,
  selectable = false,
  onClick
}: UnderlyingVaultPendingTimelockItemProps) => {
  const displayName = vault?.name ?? null

  return (
    <StyledVaultPendingTimelock.Container selectable={selectable}>
      <div
        onClick={onClick}
        data-function-name={pendingTimelock.functionName}
        data-typename={pendingTimelock.decodedData.__typename}
      >
        {vault?.name && vault.address && (
          <StyledVaultPendingTimelock.Row>
            <div className="gap-2xs flex min-w-0 flex-row items-center">
              <span className="text-2xs text-secondary-foreground">via</span>
              <InternalLink
                className="min-w-0"
                href={getVaultUrl({
                  identifier: InterfaceChainUtils.getIdentifierById(chainId),
                  vaultAddress: getAddress(vault.address),
                  vaultName: vault.name
                })}
                onClick={e => e.stopPropagation()}
              >
                <HoverableWrapper>
                  <div className="gap-xs flex min-w-0 items-center">
                    <Avatar src={vault.assetLogoURI} size="small" />
                    <TruncatedTooltip text={vault.name}>
                      {ref => (
                        <span
                          ref={ref}
                          tabIndex={-1}
                          className="text-2xs text-foreground min-w-0 truncate"
                        >
                          {displayName}
                        </span>
                      )}
                    </TruncatedTooltip>
                    <V1Badge tabIndex={-1} />
                  </div>
                </HoverableWrapper>
              </InternalLink>
            </div>
          </StyledVaultPendingTimelock.Row>
        )}
        {pendingTimelock.decodedData.__typename === 'VaultRemoveMarketPendingData' &&
          isDefined(pendingTimelock.decodedData.market) && (
            <VaultPendingTimelockRemoveMarket
              chainId={chainId}
              collateralAsset={pendingTimelock.decodedData.market.collateralAsset}
              loanAsset={pendingTimelock.decodedData.market.loanAsset}
              functionName={pendingTimelock.functionName}
              marketId={pendingTimelock.decodedData.market.marketId}
            />
          )}
        {pendingTimelock.decodedData.__typename === 'VaultSetCapPendingData' &&
          isDefined(pendingTimelock.decodedData.market) && (
            <VaultPendingTimelockChangeCap
              chainId={chainId}
              collateralAsset={pendingTimelock.decodedData.market.collateralAsset}
              loanAsset={pendingTimelock.decodedData.market.loanAsset}
              supplyCap={pendingTimelock.decodedData.supplyCap}
              currentSupplyCap={marketSupplyCaps?.[pendingTimelock.decodedData.market.marketId]}
              functionName={pendingTimelock.functionName}
              marketId={pendingTimelock.decodedData.market.marketId}
            />
          )}
        <VaultPendingTimelockCountdown validAt={pendingTimelock.validAt} />
      </div>
    </StyledVaultPendingTimelock.Container>
  )
}

export default UnderlyingVaultPendingTimelockItem
